/**
 * 共通のヘルパー関数
 * プロジェクトやエンジニアのステータス、優先度、フォーマッティングなどの
 * 汎用的な処理を提供します
 */

// ===== 型定義 =====

/** プロジェクトのステータス */
export type ProjectStatus = 'pending' | 'active' | 'completed' | 'cancelled';

/** エンジニアのステータス */
export type EngineerStatus = 'available' | 'busy' | 'unavailable';

/** 統合ステータス型 */
export type Status = ProjectStatus | EngineerStatus;

/** 優先度レベル */
export type Priority = 'urgent' | 'high' | 'medium' | 'low';

/** アサインメントステータス */
export type AssignmentStatus = 'proposed' | 'confirmed' | 'active' | 'completed' | 'cancelled';

/** カラーバリアント（Material-UIに対応） */
export type ColorVariant = 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';

// ===== ステータス関連ヘルパー =====

/**
 * ステータス値に基づいて適切な色バリアントを取得します
 * @param status - プロジェクトまたはエンジニアのステータス
 * @returns Material-UIのカラーバリアント
 * @example
 * ```typescript
 * const color = getStatusColor('active'); // 'success'
 * ```
 */
export const getStatusColor = (status: Status): ColorVariant => {
  const statusColorMap: Record<Status, ColorVariant> = {
    // プロジェクトステータス
    pending: 'warning',
    active: 'success',
    completed: 'info',
    cancelled: 'error',
    // エンジニアステータス
    available: 'success',
    busy: 'error',
    unavailable: 'default'
  };
  
  return statusColorMap[status] ?? 'default';
};

/**
 * ステータス値に基づいて日本語表示テキストを取得します
 * @param status - プロジェクトまたはエンジニアのステータス
 * @returns 日本語のステータステキスト
 * @example
 * ```typescript
 * const text = getStatusText('active'); // '募集中' または '稼働可能'
 * ```
 */
export const getStatusText = (status: Status): string => {
  const statusTextMap: Record<Status, string> = {
    // プロジェクトステータス
    pending: '準備中',
    active: '募集中',
    completed: '完了',
    cancelled: 'キャンセル',
    // エンジニアステータス
    available: '稼働可能',
    busy: '稼働中',
    unavailable: '稼働不可'
  };
  
  return statusTextMap[status] ?? '不明';
};

// ===== 優先度関連ヘルパー =====

/**
 * 優先度レベルに基づいて適切な色コードを取得します
 * @param priority - 優先度レベル
 * @returns HEXカラーコード
 * @example
 * ```typescript
 * const color = getPriorityColor('urgent'); // '#c0392b'
 * ```
 */
export const getPriorityColor = (priority: Priority): string => {
  const priorityColorMap: Record<Priority, string> = {
    urgent: '#c0392b',
    high: '#e74c3c',
    medium: '#f39c12',
    low: '#95a5a6'
  };
  
  return priorityColorMap[priority] ?? '#95a5a6';
};

/**
 * 優先度レベルに基づいて日本語表示テキストを取得します
 * @param priority - 優先度レベル
 * @returns 日本語の優先度テキスト
 * @example
 * ```typescript
 * const text = getPriorityText('high'); // '高'
 * ```
 */
export const getPriorityText = (priority: Priority): string => {
  const priorityTextMap: Record<Priority, string> = {
    urgent: '緊急',
    high: '高',
    medium: '中',
    low: '低'
  };
  
  return priorityTextMap[priority] ?? '不明';
};

// ===== アサインメントステータス関連ヘルパー =====

/**
 * アサインメントステータスに基づいて適切な色バリアントを取得します
 * @param status - アサインメントのステータス
 * @returns Material-UIのカラーバリアント
 * @example
 * ```typescript
 * const color = getAssignmentStatusColor('confirmed'); // 'success'
 * ```
 */
export const getAssignmentStatusColor = (status: AssignmentStatus): ColorVariant => {
  const assignmentStatusColorMap: Record<AssignmentStatus, ColorVariant> = {
    proposed: 'warning',
    confirmed: 'success',
    active: 'info',
    completed: 'success',
    cancelled: 'error'
  };
  
  return assignmentStatusColorMap[status] ?? 'default';
};

/**
 * アサインメントステータスに基づいて日本語表示テキストを取得します
 * @param status - アサインメントのステータス
 * @returns 日本語のアサインメントステータステキスト
 * @example
 * ```typescript
 * const text = getAssignmentStatusText('proposed'); // '提案中'
 * ```
 */
export const getAssignmentStatusText = (status: AssignmentStatus): string => {
  const assignmentStatusTextMap: Record<AssignmentStatus, string> = {
    proposed: '提案中',
    confirmed: '確定',
    active: '進行中',
    completed: '完了',
    cancelled: 'キャンセル'
  };
  
  return assignmentStatusTextMap[status] ?? '不明';
};

// ===== 数値フォーマット関連ヘルパー =====

/**
 * 数値を日本円形式でフォーマットします
 * @param amount - 金額（数値）
 * @returns 日本円形式の文字列（例: "¥1,000,000"）
 * @example
 * ```typescript
 * const formatted = formatCurrency(1000000); // "¥1,000,000"
 * ```
 */
export const formatCurrency = (amount: number): string => {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.error('formatCurrency: Invalid amount', amount);
    return '¥0'; // 安全なフォールバック
  }

  try {
    return `¥${amount.toLocaleString('ja-JP')}`;
  } catch (error) {
    console.error('formatCurrency: Formatting failed', error);
    return `¥${amount}`; // フォーマットなしのフォールバック
  }
};

/**
 * 年数を日本語形式でフォーマットします
 * @param years - 年数（数値）
 * @returns 日本語形式の年数文字列（例: "5年"）
 * @throws {Error} 無効な年数が渡された場合
 * @example
 * ```typescript
 * const formatted = formatYearsText(3); // "3年"
 * ```
 */
export const formatYearsText = (years: number): string => {
  if (typeof years !== 'number' || isNaN(years) || years < 0) {
    throw new Error('Invalid years: must be a non-negative number');
  }
  
  return `${years}年`;
};

// ===== 日付フォーマット関連ヘルパー =====

/**
 * 日付文字列を日本語形式でフォーマットします
 * @param dateString - ISO形式の日付文字列またはDateオブジェクト
 * @returns 日本語形式の日付文字列（例: "2024/12/31"）
 * @throws {Error} 無効な日付が渡された場合
 * @example
 * ```typescript
 * const formatted = formatDate('2024-12-31'); // "2024/12/31"
 * const formatted2 = formatDate(new Date()); // "2024/10/23"
 * ```
 */
export const formatDate = (dateString: string | Date): string => {
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }
    
    return date.toLocaleDateString('ja-JP');
  } catch (error) {
    throw new Error(`Failed to format date: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// ===== 文字列フォーマット関連ヘルパー =====

/**
 * 名前をイニシャル表示に変換します
 * 日本語の姓名の表示ルールに対応しています
 * @param name - フルネームの文字列
 * @returns イニシャル文字列
 * @example
 * ```typescript
 * const initials1 = formatNameInitials('田中 太郎'); // "田太" (スペース区切り)
 * const initials2 = formatNameInitials('田中'); // "田中" (2文字)
 * const initials3 = formatNameInitials('田中太郎'); // "田○郎" (3文字以上)
 * ```
 */
export const formatNameInitials = (name: string): string => {
  if (!name || typeof name !== 'string') {
    return '';
  }
  
  const trimmedName = name.trim();
  if (!trimmedName) {
    return '';
  }
  
  const names = trimmedName.split(/\s+/);
  
  if (names.length === 1) {
    // 姓のみまたはスペースなしの場合
    const singleName = names[0];
    if (singleName.length === 1) {
      return singleName;
    } else if (singleName.length === 2) {
      return singleName;
    } else {
      // 3文字以上の場合、最初 + ○ + 最後
      return singleName[0] + '○' + singleName[singleName.length - 1];
    }
  } else {
    // 姓名がスペースで分かれている場合、姓の最初の文字 + 名の最初の文字
    return names[0][0] + names[1][0];
  }
};

// ===== 後方互換性のためのエイリアス =====

/**
 * @deprecated formatNameInitialsを使用してください
 */
export const getInitials = formatNameInitials;

/**
 * @deprecated formatYearsTextを使用してください
 */
export const getYearsText = formatYearsText;